import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Upload,
  Table,
  InputNumber,
  Tag,
  Tooltip,
  Tabs,
  Progress,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  taskService,
  taskDraftReportService,
  taskCommentService,
  taskReviewService,
  taskTimelogService,
  Task,
  CreateTaskData,
  TaskSubTask,
  TaskDraftReport,
  TaskComment,
  TaskReview,
  TaskTimelog,
  SubTaskTemplate,
} from '../services/tasks';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { taskTypeService } from '../services/taskTypes';
import { taskTemplateService } from '../services/taskTemplates';
import { quotationService } from '../services/quotations';
import { invoiceService } from '../services/invoices';
import api from '../services/api';
import dayjs from 'dayjs';
import { OverviewTab } from '../components/TaskForm/OverviewTab';
import { DraftTab } from '../components/TaskForm/DraftTab';
import { CommentTab } from '../components/TaskForm/CommentTab';
import { ChecklistTab } from '../components/TaskForm/ChecklistTab';
import { ReviewTab } from '../components/TaskForm/ReviewTab';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface User {
  id: number;
  name: string;
  email: string;
}

export const TaskFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'tasks.update');
  const canDelete = hasPermission(user, 'tasks.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [subTaskTemplates, setSubTaskTemplates] = useState<SubTaskTemplate[]>([]);
  const [taskSubTasks, setTaskSubTasks] = useState<TaskSubTask[]>([]);
  const [draftReports, setDraftReports] = useState<Array<TaskDraftReport | { id: string; task_id: number; attachment: null; enabled: boolean; created_at: string; creator?: User; file: File }>>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [reviews, setReviews] = useState<TaskReview[]>([]);
  const [timelogs, setTimelogs] = useState<TaskTimelog[]>([]);
  const [commentText, setCommentText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState<number | null>(null);
  const [timelogForm, setTimelogForm] = useState({ date: dayjs(), hour: 0, description: '' });
  const [isManualAdd, setIsManualAdd] = useState<Set<string | number>>(new Set());
  // Track items to be deleted (only IDs of existing items)
  const [timelogsToDelete, setTimelogsToDelete] = useState<number[]>([]);
  const [previousStatus, setPreviousStatus] = useState<string>('Plan');
  const [taskName, setTaskName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(!isEdit);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const [uploadQueue, setUploadQueue] = useState<Array<{ file: File; id: string }>>([]); // For new tasks, default to editing mode
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadTask(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const fetchInitialData = async () => {
    try {
      const [
        customersResponse,
        businessesResponse,
        taskTypesResponse,
        taskTemplatesResponse,
        quotationsResponse,
        invoicesResponse,
        subTasksResponse,
      ] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        taskTypeService.getAll(),
        taskTemplateService.getAll(),
        quotationService.getAll(),
        invoiceService.getAll(),
        api.get('/sub-tasks').catch(() => ({ data: { data: [] } })),
      ]);

      setCustomers(customersResponse.data || []);
      setBusinesses(businessesResponse.data || []);
      setTaskTypes(taskTypesResponse.data || []);
      setTaskTemplates(taskTemplatesResponse.data || []);
      setQuotations(quotationsResponse.data || []);
      setInvoices(invoicesResponse.data || []);
      // Load all sub task templates initially for Add button
      const allSubTasks = subTasksResponse.data?.data || subTasksResponse.data || [];
      setSubTaskTemplates(allSubTasks);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      message.error('Failed to load initial data');
    }
  };

  const loadTask = async (taskId: number) => {
    setLoadingTask(true);
    try {
      const loadedTask = await taskService.getById(taskId);
      const resolvedType = (loadedTask as any).type ?? ((loadedTask as any).business_id ? 'business' : 'personal');
      const resolvedReferenceId = (loadedTask as any).reference_id ?? ((loadedTask as any).business_id ?? loadedTask.customer_id);
      form.setFieldsValue({
        ...loadedTask,
        type: resolvedType,
        reference_id: resolvedReferenceId,
        created_date: loadedTask.created_date ? dayjs(loadedTask.created_date) : undefined,
        due_date: loadedTask.due_date ? dayjs(loadedTask.due_date) : undefined,
        deadline: loadedTask.deadline ? dayjs(loadedTask.deadline) : undefined,
        plan_date: (loadedTask as any).plan_date ? dayjs((loadedTask as any).plan_date) : undefined,
      });
      
      setCustomerType(resolvedType);
      // Handle business_id
      if (resolvedType === 'business' && resolvedReferenceId) {
        form.setFieldsValue({ business_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        let business = businesses.find(b => b.id === resolvedReferenceId)
          || businesses.find(b => b.id === (loadedTask as any).business_id);
        if (!business) {
          try {
            business = await businessService.getById(resolvedReferenceId);
          } catch {
            business = undefined;
          }
        }
        if (business) {
          setSelectedBusiness(business);
          if (business.customer_id) {
            try {
              const customer = await customerService.getById(business.customer_id);
              setSelectedCustomer(customer);
              form.setFieldsValue({ customer_id: business.customer_id });
            } catch {
              // Ignore error
            }
          }
        }
      } else if (resolvedType === 'personal' && resolvedReferenceId) {
        form.setFieldsValue({ customer_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        try {
          const customer = await customerService.getById(resolvedReferenceId);
          setSelectedCustomer(customer);
        } catch {
          // Ignore error
        }
      }
      // Handle both camelCase and snake_case from backend
      const rawSubTasks = (loadedTask as any).subTasks || (loadedTask as any).sub_tasks || [];
      // Convert snake_case to camelCase for sub tasks
      const subTasks = rawSubTasks.map((st: any) => ({
        id: st.id,
        task_id: st.task_id,
        sub_task_template_id: st.sub_task_template_id,
        name: st.name,
        completed: st.completed,
        created_by: st.created_by,
        updated_by: st.updated_by,
        created_at: st.created_at,
        updated_at: st.updated_at,
        subTaskTemplate: st.subTaskTemplate || st.sub_task_template || undefined,
        creator: st.creator || undefined,
        updater: st.updater || undefined,
      }));
      setTaskSubTasks(subTasks);
      const draftReports = (loadedTask as any).draftReports || (loadedTask as any).draft_reports || [];
      setDraftReports(draftReports);
      const comments = (loadedTask as any).comments || [];
      setComments(comments);
      const reviews = (loadedTask as any).reviews || [];
      setReviews(reviews);
      const timelogs = (loadedTask as any).timelogs || [];
      setTimelogs(timelogs);
      // Calculate total hours from timelogs and set to actual_hour if not already set
      const totalHours = timelogs.reduce((total: number, t: any) => total + (t.hour || 0), 0);
      if (totalHours > 0 && !loadedTask.actual_hour) {
        form.setFieldsValue({ actual_hour: totalHours });
      }
      
      // Set previous status for Completed checkbox
      if (loadedTask.status === 'Completed') {
        setPreviousStatus('Review'); // Default previous status when task is completed
      } else {
        setPreviousStatus(loadedTask.status || 'Plan');
      }
      
      // Load sub task templates for the task's template (if exists) but don't auto-add sub tasks
      if (loadedTask.task_template_id) {
        try {
          const response = await api.get(`/sub-tasks`);
          const allSubTasks = response.data?.data || response.data || [];
          setSubTaskTemplates(allSubTasks);
        } catch {
          // Ignore error
        }
      }
      
      // Set task name immediately after loading
      setTaskName(formatTaskNumber(taskId));
    } catch {
      message.error('Failed to load task');
      navigate('/tasks');
    } finally {
      setLoadingTask(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      type,
      reference_id: undefined,
      customer_id: undefined,
      business_id: undefined,
      team_id: undefined,
    });
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          form.setFieldsValue({
            customer_id: business.customer_id,
            reference_id: businessId,
            team_id: customer.team_id || undefined,
          });
        } else {
          form.setFieldsValue({
            customer_id: undefined,
            reference_id: businessId,
            team_id: undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      setSelectedCustomer(null);
      form.setFieldsValue({
        customer_id: undefined,
        reference_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({
          team_id: customer.team_id || undefined,
          reference_id: customerId,
        });
      } catch {
        message.error('Failed to load customer details');
      }
    } else {
      form.setFieldsValue({
        team_id: undefined,
        reference_id: undefined,
      });
    }
  };

  const handleQuotationChange = async (quotationId: number) => {
    if (quotationId) {
      try {
        const quotation = await quotationService.getById(quotationId);
        form.setFieldsValue({
          staff_id: quotation.staff_id || undefined,
        });
      } catch {
        message.error('Failed to load quotation details');
      }
    } else {
      form.setFieldsValue({
        staff_id: undefined,
      });
    }
  };

  const handleTaskTemplateChange = async (templateId: number | null) => {
    if (templateId) {
      try {
        // Load sub task templates for this template
        const response = await api.get(`/sub-tasks`);
        const allSubTasks = response.data?.data || response.data || [];
        const subTasks = allSubTasks.filter((st: SubTaskTemplate) => st.task_template_id === templateId);
        // Keep all sub task templates available for Add button, not just the ones from this template
        // This allows users to continue adding sub tasks even after selecting a template
        setSubTaskTemplates(allSubTasks);
        
        if (!isEdit) {
          // If creating new task, auto-add all sub tasks from template
          const newSubTasks = subTasks.map((st: SubTaskTemplate, index: number) => ({
            id: `temp-${Date.now()}-${index}`,
            task_id: 0,
            sub_task_template_id: st.id,
            name: st.name, // Use template name as default value
            completed: false,
            subTaskTemplate: st,
          }));
          setTaskSubTasks(newSubTasks);
        } else {
          // If editing and user manually changes template, replace sub tasks with new template's sub tasks
          // Only replace if user is actively changing the template (not on initial load)
          const newSubTasks = subTasks.map((st: SubTaskTemplate, index: number) => ({
            id: `temp-${Date.now()}-${index}`,
            task_id: id ? parseInt(id, 10) : 0,
            sub_task_template_id: st.id,
            name: st.name, // Use template name as default value
            completed: false,
            subTaskTemplate: st,
          }));
          setTaskSubTasks(newSubTasks);
        }
      } catch {
        message.error('Failed to load sub task templates');
      }
    } else {
      // If no template selected, reload all sub task templates for Add button
      try {
        const response = await api.get(`/sub-tasks`);
        const allSubTasks = response.data?.data || response.data || [];
        setSubTaskTemplates(allSubTasks);
      } catch {
        // Ignore error, keep current subTaskTemplates
      }
      if (!isEdit) {
        setTaskSubTasks([]);
      }
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Calculate actual_hour from timelogs
      const calculatedActualHour = calculateTotalHours();
      
      // Determine status based on is_sent and is_completed
      let finalStatus = values.status;
      if (values.is_completed === true) {
        // If is_completed is true, set status to Completed
        finalStatus = 'Completed';
      } else if (values.is_sent === true) {
        // If is_sent is true, set status to Review (unless already Approved or Completed)
        if (values.status !== 'Approve' && values.status !== 'Completed') {
          finalStatus = 'Review';
        }
      } else if (values.is_sent === false && values.status === 'Review') {
        // If is_sent is false and current status is Review, change to Progress
        finalStatus = 'Progress';
      } else if (values.is_completed === false && values.status === 'Completed') {
        // If is_completed is false and current status is Completed, change back to Approve
        finalStatus = 'Approve';
      }
      
      const referenceId = customerType === 'business' ? values.business_id : values.customer_id;
      const payload: CreateTaskData = {
        ...values,
        created_date: values.created_date ? values.created_date.format('YYYY-MM-DD') : null,
        due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
        deadline: values.deadline ? values.deadline.format('YYYY-MM-DD') : null,
        plan_date: values.plan_date ? values.plan_date.format('YYYY-MM-DD') : null,
        // If business_id is selected, use business's customer_id
        type: customerType,
        reference_id: referenceId,
        customer_id: values.business_id ? selectedBusiness?.customer_id : values.customer_id,
        business_id: values.business_id || undefined,
        is_sent: values.is_sent !== undefined ? values.is_sent : false,
        is_completed: values.is_completed !== undefined ? values.is_completed : false,
        status: finalStatus, // Use the determined status
        actual_hour: calculatedActualHour, // Use calculated value from timelogs
        sub_tasks: taskSubTasks.map(st => ({
          sub_task_template_id: st.sub_task_template_id,
          name: st.name || undefined,
          completed: st.completed,
        })),
      };

      let taskId: number;
      if (isEdit && id) {
        await taskService.update(parseInt(id, 10), payload);
        taskId = parseInt(id, 10);
        message.success('Task updated successfully');
      } else {
        const newTask = await taskService.create(payload);
        taskId = newTask.id;
        message.success('Task created successfully');
      }

      // Save comments (only new ones with temp IDs)
      const newComments = comments.filter(c => typeof c.id === 'string' && String(c.id).startsWith('temp-'));
      for (const comment of newComments) {
        await taskCommentService.create({
          task_id: taskId,
          comment: (comment as any).comment || '',
        });
      }

      // Save reviews (only new ones with temp IDs)
      const newReviews = reviews.filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-'));
      for (const review of newReviews) {
        await taskReviewService.create({
          task_id: taskId,
          customer_id: (review as any).customer_id,
          review: (review as any).review || '',
          rating: (review as any).rating || undefined,
        });
      }

      // Save timelogs (only new ones with temp IDs)
      const newTimelogs = timelogs.filter(t => typeof t.id === 'string' && String(t.id).startsWith('temp-'));
      for (const timelog of newTimelogs) {
        await taskTimelogService.create({
          task_id: taskId,
          date: timelog.date,
          hour: timelog.hour,
          description: timelog.description || '',
        });
      }

      // Delete timelogs marked for deletion
      for (const timelogId of timelogsToDelete) {
        await taskTimelogService.delete(timelogId);
      }

      // Save draft reports (only new ones with temp IDs)
      const newDraftReports = draftReports.filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-draft-'));
      for (const report of newDraftReports) {
        const fileId = `draft-${Date.now()}-${Math.random()}`;
        setUploadQueue(prev => [...prev, { file: (report as any).file, id: fileId }]);
        setUploadStatus(prev => ({ ...prev, [fileId]: 'uploading' }));
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        
        try {
          const formData = new FormData();
          formData.append('task_id', taskId.toString());
          formData.append('attachment', (report as any).file);
          formData.append('enabled', 'true');
          
          await api.post('/task-draft-reports', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(prev => ({ ...prev, [fileId]: percent }));
              }
            },
          });
          
          setUploadStatus(prev => ({ ...prev, [fileId]: 'success' }));
          setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        } catch (error: any) {
          setUploadStatus(prev => ({ ...prev, [fileId]: 'error' }));
          throw error; // Re-throw to stop the save process
        }
      }

      // Clear upload queue after a delay
      setTimeout(() => {
        setUploadQueue([]);
        setUploadStatus({});
        setUploadProgress({});
      }, 2000);

      navigate('/tasks');
    } catch {
      message.error(isEdit ? 'Failed to update task' : 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDraftReport = (file: File) => {
    // Add to local state only, don't upload to backend yet
    const tempId = `temp-draft-${Date.now()}-${Math.random()}`;
    const newReport: any = {
      id: tempId,
      task_id: id ? parseInt(id, 10) : 0,
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
      creator: user ? { id: user.id, name: user.name, email: user.email || '' } : undefined,
      file: file, // Store the file object for later upload
    };
    
    // Add to local state
    setDraftReports(prev => [newReport, ...prev]);
    
    // Don't automatically change status to Review - user must click "Sent" checkbox
    message.success('Draft report added (will be saved on submit)');
  };

  const handleDeleteDraftReport = (reportId: number | string) => {
    // If it's a temp ID (string), just remove from local state
    if (typeof reportId === 'string' && reportId.startsWith('temp-draft-')) {
      setDraftReports(prev => prev.filter(r => {
        const rId = r.id;
        if (typeof rId === 'string') {
          return rId !== reportId;
        }
        return true; // Keep non-string IDs
      }));
      message.success('Draft report removed');
      return;
    }
    
    // If it's an existing report (number), delete immediately
    if (typeof reportId === 'number') {
      setDraftReports(prev => prev.filter(r => {
        const rId = r.id;
        if (typeof rId === 'number') {
          return rId !== reportId;
    }
        return true; // Keep non-number IDs
      }));
      // Delete from backend immediately
      taskDraftReportService.delete(reportId).then(() => {
      message.success('Draft report deleted successfully');
      }).catch(() => {
      message.error('Failed to delete draft report');
        // Reload the report if deletion failed
        if (id) {
          loadTask(parseInt(id, 10));
        }
      });
    }
  };

  const handleAddComment = () => {
    if (!commentText.trim()) {
      message.error('Please enter a comment');
      return;
    }
    // Add to local state only, will be saved on form submit
    const newComment: any = {
      id: `temp-comment-${Date.now()}`,
      task_id: id ? parseInt(id, 10) : 0,
      comment: commentText,
      created_at: new Date().toISOString(),
      user: { name: user?.name || 'Current User' }, // Display current logged in user
    };
    setComments(prev => [...prev, newComment]);
    setCommentText('');
    message.success('Comment added (will be saved on submit)');
  };

  const handleAddReview = () => {
    if (!reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }
    // Add to local state only, will be saved on form submit
    const newReview: any = {
      id: `temp-review-${Date.now()}`,
      task_id: id ? parseInt(id, 10) : 0,
      customer_id: undefined,
      review: reviewText,
      rating: reviewRating ?? undefined,
      created_at: new Date().toISOString(),
      creator: { name: user?.name || 'Current User' }, // Display current logged in user
    };
    setReviews(prev => [...prev, newReview]);
    setReviewText('');
    setReviewRating(null);
    message.success('Review added (will be saved on submit)');
  };

  const handleDeleteComment = (commentId: number | string) => {
    if (typeof commentId === 'number') {
      // Mark for deletion if it's an existing comment
      // For now, just remove from local state
    }
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  const handleDeleteReview = (reviewId: number | string) => {
    if (typeof reviewId === 'number') {
      // Mark for deletion if it's an existing review
      // For now, just remove from local state
    }
    setReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  const handleAddTimelog = () => {
    if (!timelogForm.date || !timelogForm.hour) {
      message.error('Please enter date and hour');
      return;
    }
    // Add to local state only, will be saved on form submit
    const newTimelog: any = {
      id: `temp-timelog-${Date.now()}`,
      task_id: id ? parseInt(id, 10) : 0,
      date: timelogForm.date.format('YYYY-MM-DD'),
      hour: timelogForm.hour,
      description: timelogForm.description,
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setTimelogs(prev => [...prev, newTimelog]);
    // Update actual_hour with total hours from timelogs
    const totalHours = [...timelogs, newTimelog].reduce((total, t) => total + (t.hour || 0), 0);
    form.setFieldsValue({ actual_hour: totalHours });
    setTimelogForm({ date: dayjs(), hour: 0, description: '' });
    message.success('Timelog added (will be saved on submit)');
  };

  const handleDeleteTimelog = (timelogId: number | string) => {
    // If it's a temporary ID (string), just remove from local state
    // If it's an existing ID (number), mark for deletion
    let updatedTimelogs: TaskTimelog[] = [];
    if (typeof timelogId === 'string' && timelogId.startsWith('temp-')) {
      updatedTimelogs = timelogs.filter(t => String(t.id) !== timelogId);
      setTimelogs(updatedTimelogs);
    } else {
      // Mark existing timelog for deletion
      setTimelogsToDelete(prev => [...prev, timelogId as number]);
      updatedTimelogs = timelogs.filter(t => t.id !== timelogId);
      setTimelogs(updatedTimelogs);
    }
    // Update actual_hour with total hours from remaining timelogs
    const totalHours = updatedTimelogs.reduce((total, t) => total + (t.hour || 0), 0);
    form.setFieldsValue({ actual_hour: totalHours });
    message.success('Timelog removed (will be deleted on submit)');
  };

  // Helper function to get display value for view mode
  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    const value = form.getFieldValue(fieldName);
    if (!value && value !== 0) return '-';
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value || opt.value === value);
      return option?.name || option?.label || value;
    }
    
    if (type === 'date') {
      if (!value) return '-';
      // Handle dayjs object or string
      const dateValue = dayjs.isDayjs(value) ? value : dayjs(value);
      return dateValue.isValid() ? dateValue.format('DD/MM/YYYY') : '-';
    }
    
    if (type === 'number') {
      return value || value === 0 ? value.toString() : '-';
    }
    
    return value || '-';
  };

  const formatTaskNumber = (taskId: number) => {
    return `#${taskId.toString().padStart(7, '0')}`;
  };

  const getTaskDisplayName = () => {
    if (isEdit && id) {
      return formatTaskNumber(parseInt(id, 10));
    }
    return 'New Task';
  };

  useEffect(() => {
    if (isEdit && id && customers.length > 0 && taskTypes.length > 0) {
      const displayName = getTaskDisplayName();
      setTaskName(displayName);
    }
  }, [isEdit, id, customers, taskTypes, form.getFieldValue('customer_id'), form.getFieldValue('task_type_id')]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        created_date: undefined,
        status: 'Plan',
        enabled: true,
      });
      setPreviousStatus('Plan');
    }
  }, [isEdit, form]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const handleCreatedDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      // When created_date is set, automatically change status to "Progress"
      const currentStatus = form.getFieldValue('status');
      if (!currentStatus || currentStatus === 'Plan') {
        form.setFieldsValue({ status: 'Progress' });
        setPreviousStatus('Progress');
      }
    } else {
      // If created_date is cleared, revert status to "Plan" when it was "Progress"
      const currentStatus = form.getFieldValue('status');
      if (currentStatus === 'Progress') {
        form.setFieldsValue({ status: 'Plan' });
        setPreviousStatus('Plan');
      }
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const calculateTotalHours = () => {
    return timelogs.reduce((total, timelog) => total + (timelog.hour || 0), 0);
  };

  const getReferencePayload = () => ({
    type: form.getFieldValue('type') || customerType,
    reference_id: form.getFieldValue('reference_id'),
    customer_id: form.getFieldValue('customer_id'),
    business_id: form.getFieldValue('business_id'),
  });

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await taskService.delete(parseInt(id, 10));
                    message.success('Task deleted');
                    navigate('/tasks');
    } catch {
      message.error('Failed to delete task');
                }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? (taskName || 'Loading...') : 'New Task'}
          </Title>
          <Space>
            {isEdit && !isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {isEdit && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loadingTask || (isEdit && !isEditing)}
          onValuesChange={(changedValues) => {
            if (changedValues.customer_id !== undefined || changedValues.task_type_id !== undefined) {
              const displayName = getTaskDisplayName();
              setTaskName(displayName);
            }
          }}
        >
        <Form.Item name="type" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="reference_id" hidden>
          <Input />
        </Form.Item>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status} noStyle>
              {({ getFieldValue }) => {
                const currentStatus = getFieldValue('status') || 'Plan';
                const statuses: Array<'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed'> = ['Plan', 'Progress', 'Review', 'Approve', 'Completed'];
                const statusConfig = {
                  Plan: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff' },
                  Progress: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00' },
                  Review: { bg: '#F9F0FF', border: '#9254DE', color: '#722ED1', dot: '#722ED1' },
                  Approve: { bg: '#E6FFFB', border: '#36CFC9', color: '#13C2C2', dot: '#13C2C2' },
                  Completed: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A' },
                };
                
                return (
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = currentStatus === status;
                      const config = statusConfig[status];
                      return (
                        <div
                          key={status}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {status}
                        </div>
                      );
                    })}
                  </Space>
                );
              }}
            </Form.Item>
          }
        >
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <OverviewTab
              form={form}
              isEditing={isEditing}
              customerType={customerType}
              customers={customers}
              businesses={businesses}
              taskTypes={taskTypes}
              taskTemplates={taskTemplates}
              quotations={quotations}
              timelogs={timelogs}
              isEdit={isEdit}
              id={id}
              handleCustomerTypeChange={handleCustomerTypeChange}
              handleCustomerChange={handleCustomerChange}
              handleBusinessChange={handleBusinessChange}
              handleTaskTemplateChange={handleTaskTemplateChange}
              handleQuotationChange={handleQuotationChange}
              handleCreatedDateChange={handleCreatedDateChange}
              getDisplayValue={getDisplayValue}
              calculateTotalHours={calculateTotalHours}
              timelogForm={timelogForm}
              setTimelogForm={setTimelogForm}
              handleAddTimelog={handleAddTimelog}
              handleDeleteTimelog={handleDeleteTimelog}
              user={user || undefined}
            />
          </TabPane>

          {/* Checklist Tab */}
          <TabPane tab="Checklist" key="progress">
            <ChecklistTab
              isEdit={isEdit}
              id={id}
              taskSubTasks={taskSubTasks}
              setTaskSubTasks={setTaskSubTasks}
              subTaskTemplates={subTaskTemplates}
              setSubTaskTemplates={setSubTaskTemplates}
              isManualAdd={isManualAdd}
              setIsManualAdd={setIsManualAdd}
              user={user || undefined}
              formatDateTime={formatDateTime}
              getReferencePayload={getReferencePayload}
            />
          </TabPane>

          {/* Draft Tab */}
          <TabPane tab="Draft" key="draft">
            <DraftTab
              isEdit={isEdit}
              draftReports={draftReports}
              uploadQueue={uploadQueue}
              uploadStatus={uploadStatus}
              uploadProgress={uploadProgress}
              handleAddDraftReport={handleAddDraftReport}
              handleDeleteDraftReport={handleDeleteDraftReport}
              formatDateTime={formatDateTime}
            />
          </TabPane>

          {/* Comment Tab */}
          <TabPane tab="Comment" key="comment">
            <CommentTab
              isEdit={isEdit}
              comments={comments}
              commentText={commentText}
              setCommentText={setCommentText}
              handleAddComment={handleAddComment}
              handleDeleteComment={handleDeleteComment}
              formatDateTime={formatDateTime}
            />
          </TabPane>

          {/* Review Tab */}
          <TabPane tab="Review" key="review">
            <ReviewTab
              isEdit={isEdit}
              reviews={reviews}
              reviewText={reviewText}
              reviewRating={reviewRating}
              setReviewText={setReviewText}
              setReviewRating={setReviewRating}
              handleAddReview={handleAddReview}
              handleDeleteReview={handleDeleteReview}
              formatDateTime={formatDateTime}
            />
          </TabPane>
        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        }}>
          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.is_sent !== currentValues.is_sent || prevValues.is_completed !== currentValues.is_completed || prevValues.status !== currentValues.status} noStyle>
            {({ getFieldValue }) => {
              const currentStatus = getFieldValue('status');
              const isSent = getFieldValue('is_sent') || false;
              const isCompleted = getFieldValue('is_completed') || false;
              // Disable Sent checkbox if status is Approve or Completed, or if not editing
              const isSentDisabled = (!isEditing && isEdit) || currentStatus === 'Approve' || currentStatus === 'Completed';
              // Disable Completed checkbox if status is not Approve, or if not editing
              const isCompletedDisabled = (!isEditing && isEdit) || currentStatus !== 'Approve';
              
              return (
                <Space>
                  <Form.Item name="is_sent" valuePropName="checked" noStyle>
                    <Checkbox 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Form.Item will automatically update is_sent
                        // We just need to update status
                        if (checked) {
                          form.setFieldsValue({ status: 'Review' });
                        } else {
                          // If unchecking and not approved yet, revert to Progress
                          form.setFieldsValue({ status: 'Progress' });
                        }
                      }}
                      disabled={isSentDisabled}
                    >
                      Sent
                    </Checkbox>
                  </Form.Item>
                  <Form.Item name="is_completed" valuePropName="checked" noStyle>
                    <Checkbox 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Form.Item will automatically update is_completed
                        // We just need to update status
                        if (checked) {
                          form.setFieldsValue({ status: 'Completed' });
                        } else {
                          // If unchecking, revert to Approve
                          form.setFieldsValue({ status: 'Approve' });
                        }
                      }}
                      disabled={isCompletedDisabled}
                    >
                      Completed
                    </Checkbox>
                  </Form.Item>
                </Space>
              );
            }}
          </Form.Item>
          <Space>
          {(isEditing || !isEdit) && (
            <>
              <Button 
                onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload task data to reset form
                  loadTask(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/tasks');
                }
                }}
                disabled={false}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          </Space>
        </div>
      </Form>
      
      {/* Buttons outside Form to avoid disabled state */}
      {!isEditing && isEdit && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Button onClick={() => navigate('/tasks')}>Cancel</Button>
        </div>
      )}
    </Card>
  );
};
